import React, { useRef, useEffect, useCallback, useState } from "react";
import { Container } from "reactstrap";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import UniversalLife from "../../Components/UniversalLife/UniversalLife";
import TermLife from "../../Components/TermLife/TermLife";
import CriticalIllness from "../../Components/CriticalIllness/CriticalIllness";
import "./test.css";
import { useTranslation } from "../../Hooks/useTranslation"; // Import the hook

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

export const OurDesktopProducts = ({ handleFooter, callBackSection }) => {
  const translations = useTranslation(); // Get translations dynamically
  const timelines = useRef([]);
  const currentSection = useRef(0); // Track the current active section
  const sectionsRef = useRef([]);
  const [activeSection, setActiveSection] = useState(0); // Add state for active section

  const initScroll = useCallback((section, items, direction) => {
    // Set initial states for all items except the first one.
    items.forEach((item, index) => {
      if (index !== 0) {
        gsap.set(item, { opacity: 0, zIndex: -1 });
      }
    });

    // Create a single ScrollTrigger for the entire section
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        pin: true,
        start: "top+150 top",
        end: () => `+=${items.length * 100}%`,
        scrub: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const progress = self.progress;
          const sectionIndex = Math.floor(progress * items.length);
          if (sectionIndex >= 0 && sectionIndex < items.length) {
            setActiveSection(sectionIndex);
          }
        },
      },
      defaults: { ease: "none" },
    });

    items.forEach((item, index) => {
      if (index !== items.length - 1) {
        timeline.to(item, {
          opacity: 0,
          zIndex: -1,
          duration: 0.01,
        });
      }
      timeline.to(
        items[index + 1],
        {
          opacity: 1,
          duration: 1,
          zIndex: 1,
        },
        "<"
      );
    });

    // Store the timeline
    timelines.current.push(timeline);
  }, []);

  useEffect(() => {
    const scrollSection = document.querySelectorAll(".scroll-section");
    scrollSection.forEach((section) => {
      const wrapper = section.querySelector(".wrapper");
      const items = wrapper.querySelectorAll(".item");

      let direction = null;
      if (section.classList.contains("vertical-section")) {
        direction = "vertical";
      } else if (section.classList.contains("horizontal-section")) {
        direction = "horizontal";
      }
      initScroll(section, items, direction);
    });

    // Store references to the sections
    sectionsRef.current = document.querySelectorAll(".list .item");

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, [initScroll]);

  const handleClick = (index) => {
    if (timelines.current[0]) {
      const progress = index / sectionsRef.current.length;
      const scrollTo =
        timelines.current[0].scrollTrigger.start +
        (timelines.current[0].scrollTrigger.end -
          timelines.current[0].scrollTrigger.start) *
          progress;

      gsap.to(window, {
        duration: 1,
        scrollTo: scrollTo,
        overwrite: "auto",
      });
    }
  };

  return (
    <div
      id="our-products"
      className="scroll-section vertical-section section py-16 lg:py-0"
    >
      <div>
        <Container>
          <div className="our-product-header-tabs lg:top-[190px] relative z-[2]">
            <div className=" animate__animated  mt-0  animate__fadeInLeft max-w-[420px] lg:pl-0 pl-4">
              <h3 className="text-[2.5rem] leading-[2.8rem] font-semibold  lg:mb-5">
                {translations.ourProductHeading}
              </h3>
              <p className="lg:hidden block">
                Discover the perfect product for you.
              </p>
              <div className="active-section flex lg:justify-center justify-space-between gap-2 bg-white rounded-full lg:mt-4 mb-4 mt-7">
                <button
                  onClick={() => handleClick(0)}
                  className={`flex-1 content text-[14px] leading-[20px] py-[10px] lg:px-4 px-2 rounded-full w-1/3 ${
                    activeSection === 0 ? "active-button-state" : ""
                  }`}
                >
                  {translations.universalLifeTab}
                </button>
                <button
                  onClick={() => handleClick(1)}
                  className={`flex-1 content text-[14px] leading-[20px] py-[10px] lg:px-4 px-2 rounded-full w-1/3 ${
                    activeSection === 1 ? "active-button-state" : ""
                  }`}
                >
                  {translations.termLifeTab}
                </button>
                <button
                  onClick={() => handleClick(2)}
                  className={`flex-2 content text-[14px] leading-[20px] py-[10px] lg:px-4 px-2 rounded-full w-1/3 ${
                    activeSection === 2 ? "active-button-state" : ""
                  }`}
                >
                  {translations.criticalIllnessTab}
                </button>
              </div>
            </div>
          </div>
        </Container>
      </div>
      <div className="wrapper">
        <div className="list slides-wrapper">
          <UniversalLife
            key="universal"
            className="item"
            sectionActive={activeSection === 0}
          />
          <TermLife
            key="term"
            className="item"
            sectionActive={activeSection === 1}
          />
          <CriticalIllness
            key="critical"
            className="item"
            sectionActive={activeSection === 2}
          />
        </div>
      </div>
    </div>
  );
};

export default OurDesktopProducts;
